// Issue #329: pagina de gestao de clientes do contador.
// Acessivel para admin/super_user/contador (guard no App.tsx). Lista clientes
// da org ativa, permite criar, editar e desativar (soft-delete).

import { useCallback, useEffect, useState } from 'react';
import {
  type Cliente,
  createCliente,
  deactivateCliente,
  listClientes,
  updateCliente,
} from '../services/api';
import { useClienteAtivo } from '../contexts/ClienteAtivoContext';
import { useT } from '../i18n';
// Issue #550: Page shell — remove wrapper proprio (max-w-5xl px-4 py-8
// duplicava o <main> do Layout) e padroniza o header da pagina.
import Page from '../components/Page';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

const EMPTY_FORM = { nome: '', cnpj: '', email: '', telefone: '' };

function Clientes() {
  const t = useT();
  const { refreshClientes } = useClienteAtivo();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Modal de criacao/edicao
  const [editing, setEditing] = useState<Cliente | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Confirmacao de desativacao
  const [deactivateTarget, setDeactivateTarget] = useState<Cliente | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listClientes(showInactive);
      setClientes(res.clientes ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar clientes.');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
    setSuccess(null);
  }, []);

  const openEdit = useCallback((c: Cliente) => {
    setEditing(c);
    setForm({
      nome: c.nome,
      cnpj: c.cnpj ?? '',
      email: c.email ?? '',
      telefone: c.telefone ?? '',
    });
    setShowForm(true);
    setSuccess(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!form.nome.trim()) {
      setError('Nome do cliente e obrigatorio.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        nome: form.nome.trim(),
        cnpj: form.cnpj.trim() || null,
        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
      };
      if (editing) {
        await updateCliente(editing.id, payload);
        setSuccess(`Cliente "${payload.nome}" atualizado.`);
      } else {
        await createCliente(payload);
        setSuccess(`Cliente "${payload.nome}" criado.`);
      }
      setShowForm(false);
      await fetchClientes();
      await refreshClientes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar cliente.');
    } finally {
      setSaving(false);
    }
  }, [form, editing, fetchClientes, refreshClientes]);

  const handleDeactivate = useCallback(async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    setError(null);
    try {
      await deactivateCliente(deactivateTarget.id);
      setSuccess(`Cliente "${deactivateTarget.nome}" desativado.`);
      setDeactivateTarget(null);
      await fetchClientes();
      await refreshClientes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao desativar cliente.');
    } finally {
      setDeactivating(false);
    }
  }, [deactivateTarget, fetchClientes, refreshClientes]);

  const handleReactivate = useCallback(async (c: Cliente) => {
    try {
      await updateCliente(c.id, { is_active: true });
      setSuccess(`Cliente "${c.nome}" reativado.`);
      await fetchClientes();
      await refreshClientes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao reativar cliente.');
    }
  }, [fetchClientes, refreshClientes]);

  return (
    <Page
      title="Clientes"
      subtitle="Separe os documentos fiscais por cliente. Selecione um cliente no seletor do topo para filtrar todas as telas (documentos, exportacao)."
      actions={
        <>
          <label className="flex items-center gap-2 text-sm text-brand-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-brand-300"
            />
            Mostrar inativos
          </label>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
          >
            + Novo cliente
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-md border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {loading ? (
        <p className="text-brand-600">Carregando...</p>
      ) : clientes.length === 0 ? (
        <p className="text-brand-600">Nenhum cliente cadastrado. Clique em "Novo cliente".</p>
      ) : (
        // Issue #550: tabela no card padrao (border + bg-white + shadow-sm).
        <div className="overflow-x-auto rounded-lg border border-brand-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-brand-200 text-sm">
            <thead className="bg-brand-50 text-left text-brand-700">
              <tr>
                <th className="px-4 py-3 font-semibold">Nome</th>
                <th className="px-4 py-3 font-semibold">CNPJ</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Telefone</th>
                <th className="px-4 py-3 font-semibold text-center">Docs</th>
                <th className="px-4 py-3 font-semibold text-center">Status</th>
                <th className="px-4 py-3 font-semibold">Criado em</th>
                <th className="px-4 py-3 font-semibold text-right">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-100 bg-white">
              {clientes.map((c) => (
                <tr key={c.id} className="hover:bg-brand-50">
                  <td className="px-4 py-3 font-medium text-brand-800">{c.nome}</td>
                  <td className="px-4 py-3 text-brand-600">{c.cnpj ?? '—'}</td>
                  <td className="px-4 py-3 text-brand-600">{c.email ?? '—'}</td>
                  <td className="px-4 py-3 text-brand-600">{c.telefone ?? '—'}</td>
                  <td className="px-4 py-3 text-center text-brand-600">{c.docs_count}</td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold ${
                        c.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {c.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-brand-600">{formatDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(c)}
                        className="text-brand-700 underline hover:text-brand-900"
                      >
                        Editar
                      </button>
                      {c.is_active ? (
                        <button
                          type="button"
                          onClick={() => setDeactivateTarget(c)}
                          className="text-red-600 underline hover:text-red-800"
                        >
                          Desativar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleReactivate(c)}
                          className="text-green-700 underline hover:text-green-900"
                        >
                          Reativar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de criacao/edicao */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold text-brand-800">
              {editing ? 'Editar cliente' : 'Novo cliente'}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-brand-700">Nome *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="mt-1 w-full rounded-md border border-brand-300 px-3 py-2 text-sm"
                  placeholder="Nome do cliente"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-700">CNPJ</label>
                <input
                  type="text"
                  value={form.cnpj}
                  onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                  className="mt-1 w-full rounded-md border border-brand-300 px-3 py-2 text-sm"
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-700">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full rounded-md border border-brand-300 px-3 py-2 text-sm"
                  placeholder="cliente@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-700">Telefone</label>
                <input
                  type="text"
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  className="mt-1 w-full rounded-md border border-brand-300 px-3 py-2 text-sm"
                  placeholder="(00) 0000-0000"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmacao de desativacao */}
      {deactivateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-2 text-lg font-semibold text-brand-800">Desativar cliente</h2>
            <p className="mb-4 text-sm text-brand-600">
              Desativar "{deactivateTarget.nome}"? Os documentos associados nao
              serao removidos. O cliente podera ser reativado depois.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeactivateTarget(null)}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={deactivating}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deactivating ? 'Desativando...' : 'Desativar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}

export default Clientes;
